import Button from '@ingka/button';
import documentCheckmark from '@ingka/ssr-icon/paths/document-checkmark';
import document from '@ingka/ssr-icon/paths/document';
import { TType } from '../../hooks/useTranslation';
import {
  booleanSelectOptions,
  defaultRenderer,
  getBooleanColumn,
  getCrpColumns,
  getFormattedDate,
  getOptionsForTable,
  getSolutionsLabelTextForGrid,
  getUnitTypeValue,
  includesValueOperator,
  returnSingleSelectColumn,
  textColumn,
  confidentialityColourMap,
} from '../ProjectListTable/utils';
import {
  FY_BUDGET_FILTER_FIELD,
  fyBudgetFilterOperator,
} from '../ProjectListTable/toolbar/FYBudgetFilter';
import { getFY } from '../../utils/calculation';
import { FUTURE_FORECAST_FYS } from '../../store/utils/utils';
import { FDA_OPTIONS } from '../ApprovalModalPartOfFda/utils';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { projectFromReltp } from '../../utils/fieldEditingRules';
import { returnNumberColumn } from '../ProjectListTable/utils';
import { SolutionPuposesEnum } from '../../models/selectOptionsModels';
import { PPM_BUSINESS_UNITS } from '../../store/selectData/utils';
import Typography from '@mui/material/Typography';
import accounting from 'accounting-js';
import { EUR } from '../../constants';
import Badge from '@ingka/badge';
import IngkaTooltip from '../IngkaTooltip';

export const getTranslatedValue = (value: any, namespace: string, t: TType) =>
  value ? t(`${namespace}.${value}`) : '';

export const getFdaValue = (value: any, t: TType) => {
  if (value === FDA_OPTIONS.YES) return t('common.yes');
  if (value === FDA_OPTIONS.NO) return t('common.no');
  if (value === FDA_OPTIONS.UNDER_DISCUSSION)
    return t('common.underDiscussion');
  return '';
};
const fdaTableOptions = (t) => [
  {
    label: t('common.yes'),
    value: t('common.yes'),
  },
  {
    label: t('common.no'),
    value: t('common.no'),
  },
  {
    label: t('common.underDiscussion'),
    value: t('common.underDiscussion'),
  },
];
const FinancialColumns = [
  'totalBudget',
  'totalLand',
  'totalProperty',
  'totalRetail',
  'totalRetailCost',
  'totalBudgetAllFY',
];

const columnsByFy = () => {
  const columns = [];
  for (let i = 0; i < 6; i++) {
    columns.push(
      ...[
        `land-${getFY(i)}`,
        `property-${getFY(i)}`,
        `retail-${getFY(i)}`,
        `retailCost-${getFY(i)}`,
        `total-${getFY(i)}`,
      ],
    );
  }
  return columns;
};

const NextFyForecast = [
  'forecastLandCost',
  'forecastPropertyInvestment',
  'forecastRetailInvestment',
  'forecastRetailCost',
  'totalForecast',
  'totalReleased',
  'totalActuals',
];

const FUTURE_FORECAST_FIELDS = [
  'forecastLandCost',
  'forecastPropertyInvestment',
  'forecastRetailInvestment',
  'forecastRetailCost',
];

const FutureFyForecastColumns = FUTURE_FORECAST_FYS.flatMap((fy) =>
  FUTURE_FORECAST_FIELDS.map((field) => `${field}${fy}`),
);

const getFinancialColumns = (t: TType) => {
  return [...FinancialColumns, ...columnsByFy()].map((column) => {
    return returnNumberColumn(
      t(`ppm.columns.${column.split('-')[0]}`) +
        ` ${column.split('-')[1] || ''}`,
      column,
      100,
    );
  });
};

const getNextForecastColumns = (t: TType) => {
  return [...NextFyForecast].map((column) => {
    return returnNumberColumn(
      t(`ppm.columns.${column.split('-')[0]}`) +
        ` ${column.split('-')[1] || ''}`,
      column,
      100,
    );
  });
};

const getFutureFyForecastColumns = (t: TType) => {
  return FutureFyForecastColumns.map((column) =>
    returnNumberColumn(t(`ppm.columns.${column}`), column, 100),
  );
};

export const returnApprovalTableColumns = (
  t: TType,
  countries = [],
  directiveClickedEvent?: (id: string) => void,
) => [
  returnSingleSelectColumn(
    'projectStatus',
    t('ppm.columns.projectStatus'),
    getOptionsForTable(t, 'statuses', true),
    'statuses',
    t,
    true,
  ),
  textColumn('referenceId', t('ppm.columns.referenceId'), 200),
  textColumn('projectName', t('ppm.columns.projectName'), 300),
  {
    field: 'countryName',
    headerName: t('ppm.columns.country'),
    aggregable: false,
    minWidth: 230,
    display: 'flex',
    type: 'singleSelect',
    valueOptions: countries.map((c) => ({
      value: c,
      label: c,
    })),
    renderCell: defaultRenderer,
  },
  textColumn('city', t('ppm.columns.city'), 100),
  textColumn('mtoolId', t('ppm.columns.mtoolId'), 150),
  {
    field: 'etrAndSynced',
    headerName: 'ETR Synced',
    width: 120,
    type: 'singleSelect',
    valueOptions: [
      { value: 'synced', label: 'YES' },
      { value: 'notSynced', label: 'NO' },
      { value: 'notApplicable', label: 'Not applicable' },
    ],
    groupable: false,
    valueGetter: (value, params) => {
      if (projectFromReltp(params?.projectSource)) {
        return params?.etrSynced ? 'synced' : 'notSynced';
      }
      return 'notApplicable';
    },
    groupingValueGetter: (value, params) => {
      if (projectFromReltp(params?.projectSource)) {
        return params?.etrSynced ? 'synced' : 'notSynced';
      }
      return 'notApplicable';
    },
  },
  returnSingleSelectColumn(
    'establishmentType',
    t('labels.establishmentType'),
    getOptionsForTable(t, 'establishmentTypes', true),
    'establishmentTypes',
    t,
  ),
  {
    field: 'unitType',
    headerName: t('ppm.columns.unitType'),
    aggregable: false,
    minWidth: 230,
    display: 'flex',
    type: 'singleSelect',
    valueOptions: getOptionsForTable(t, 'unitTypes', true),
    valueGetter: (params) => getUnitTypeValue(params, t),
    groupingValueGetter: (params) => getUnitTypeValue(params, t),
    renderCell: defaultRenderer,
  },
  returnSingleSelectColumn(
    'etrProjectPrioritization',
    'Project prioritisation',
    getOptionsForTable(t, 'etrProjectPrioritization', true),
    'etrProjectPrioritization',
    t,
  ),
  textColumn('openingFYTertial', 'Delivery FY-Tertial', 200),
  textColumn('businessUnitId', 'BU ID', 150),
  textColumn('locationName', 'Location name', 200),
  {
    field: 'lastUpdatedDate',
    headerName: 'Last Updated Date',
    type: 'dateTime',
    groupable: false,
    aggregable: false,
    minWidth: 150,
    display: 'flex',
    valueGetter: (value) => (value ? new Date(value) : null),
    valueFormatter: (value) => (value ? getFormattedDate(value) : ''),
    renderCell: defaultRenderer,
  },
  textColumn('lastUpdatedUser', 'Last Updated User', 200),
  returnSingleSelectColumn(
    'unitCategory',
    'Unit category',
    getOptionsForTable(t, 'unitCategories', true),
    'unitCategories',
    t,
  ),
  returnSingleSelectColumn(
    'businessUnitType',
    'BU Type',
    PPM_BUSINESS_UNITS,
    '',
    t,
  ),
  textColumn('projectOwner', 'Project owner', 200),
  textColumn('projectOrderer', 'Project orderer', 200),
  textColumn('countryProjectResponsible', 'Country project responsible', 200),
  {
    field: 'solutionCategory',
    headerName: 'Solution category',
    aggregable: false,
    minWidth: 230,
    display: 'flex',
    type: 'singleSelect',
    valueOptions: getOptionsForTable(t, 'solutionCategories', true),
    valueGetter: (value) =>
      value ? getSolutionsLabelTextForGrid(value, t('solutionCategories')) : '',
    groupingValueGetter: (value) =>
      value ? t(`solutionCategories.${value}`) : '',
    renderCell: defaultRenderer,
  },
  {
    field: 'solutionType',
    headerName: 'Solution type',
    aggregable: false,
    minWidth: 230,
    display: 'flex',
    type: 'singleSelect',
    valueOptions: getOptionsForTable(t, 'solutionTypesGrid', true),
    valueGetter: (value) =>
      value
        ? getSolutionsLabelTextForGrid(value, {
            ...(t('solutionTypesGrid') as unknown as object),
            ...(t('solutionTypes') as unknown as object),
          })
        : '',
    groupingValueGetter: (value) =>
      value ? t(`solutionTypesGrid.${value}`) : '',
    renderCell: defaultRenderer,
  },
  {
    field: 'solutionPurpose',
    headerName: 'Solution purpose',
    minWidth: 200,
    type: 'singleSelect',
    valueOptions: getOptionsForTable(t, 'solutionPuposes', true),
    filterOperators: [includesValueOperator],
    valueGetter: (value) =>
      value
        ?.split(', ')
        .map((v) =>
          getTranslatedValue(SolutionPuposesEnum[v] || v, 'solutionPuposes', t),
        )
        .join(', '),
    groupingValueGetter: (value) =>
      value
        ?.split(', ')
        .map((v) =>
          getTranslatedValue(SolutionPuposesEnum[v] || v, 'solutionPuposes', t),
        )
        .join(', '),
    renderCell: defaultRenderer,
  },
  {
    field: 'isepDate',
    headerName: 'ISEP Date',
    groupable: false,
    aggregable: false,
    minWidth: 150,
    display: 'flex',
    valueGetter: (value) => getFormattedDate(value),
    renderCell: defaultRenderer,
  },
  returnSingleSelectColumn(
    'isepStatus',
    'ISEP Status',
    getOptionsForTable(t, 'ppm.isepStatuses', true),
    'ppm.isepStatuses',
    t,
    true,
  ),
  textColumn('unifierId', 'Unifier ID', 200),
  {
    field: 'targetProjectStartDate',
    headerName: t('ppm.columns.startDate'),
    groupable: false,
    aggregable: false,
    minWidth: 150,
    display: 'flex',
    valueGetter: (value) => getFormattedDate(value),
    renderCell: defaultRenderer,
  },
  {
    field: 'targetProjectEndDate',
    headerName: t('ppm.columns.openingDate'),
    groupable: false,
    aggregable: false,
    minWidth: 150,
    display: 'flex',
    valueGetter: (value) => getFormattedDate(value),
    renderCell: defaultRenderer,
  },
  {
    field: 'projectEndDateFY',
    headerName: t('ppm.columns.projectEndDateFY'),
    groupable: true,
    aggregable: false,
    minWidth: 150,
    display: 'flex',
    valueGetter: (_value, row) =>
      row?.targetProjectEndDate
        ? getFY(0, new Date(row.targetProjectEndDate))
        : '',
    groupingValueGetter: (_value, row) =>
      row?.targetProjectEndDate
        ? getFY(0, new Date(row.targetProjectEndDate))
        : '',
    renderCell: defaultRenderer,
  },
  {
    field: 'projectClosingDate',
    headerName: 'Project closing date',
    groupable: false,
    aggregable: false,
    minWidth: 150,
    display: 'flex',
    valueGetter: (value) => getFormattedDate(value),
    renderCell: defaultRenderer,
  },
  returnSingleSelectColumn(
    'siteType',
    'Site type',
    getOptionsForTable(t, 'siteTypes', true),
    'siteTypes',
    t,
  ),
  returnSingleSelectColumn(
    'transactionType',
    t('ppm.columns.transactionType'),
    getOptionsForTable(t, 'transactionTypes', true),
    'transactionTypes',
    t,
  ),
  textColumn('leasePeriod', 'External Lease Period', 200),
  getBooleanColumn(
    'isLeasedFromAnotherIngkaCompany',
    'Leased from another Ingka company',
    t,
  ),
  {
    field: 'fyBaselineAmount',
    headerName: 'FY27 Baseline amount K Euro',
    type: 'number',
    minWidth: 200,
    display: 'flex',
    groupable: false,
    aggregable: true,
    valueGetter: (_value, row) => {
      const baseline = row?.financialSummaryInfos?.find(
        (info) =>
          info.financialCategory === 'BASELINE' && info.fiscalYear === 'FY27',
      );
      return baseline?.totalInvestmentEur
        ? Math.round(baseline.totalInvestmentEur / 1000)
        : 0;
    },
    renderCell: (params: GridRenderCellParams) => (
      <Typography noWrap alignSelf={'anchor-center'}>
        {accounting.formatMoney(params.value, {
          precision: 0,
          symbol: EUR,
        })}
      </Typography>
    ),
  },
  {
    field: 'fy26BaselineAmount',
    headerName: 'FY26 Baseline amount K Euro',
    type: 'number',
    minWidth: 200,
    display: 'flex',
    groupable: false,
    aggregable: true,
    valueGetter: (_value, row) => {
      const baseline = row?.financialSummaryInfos?.find(
        (info) =>
          info.financialCategory === 'BASELINE' && info.fiscalYear === 'FY26',
      );
      return baseline?.totalInvestmentEur
        ? Math.round(baseline.totalInvestmentEur / 1000)
        : 0;
    },
    renderCell: (params: GridRenderCellParams) => (
      <Typography noWrap alignSelf={'anchor-center'}>
        {accounting.formatMoney(params.value, {
          precision: 0,
          symbol: EUR,
        })}
      </Typography>
    ),
  },
  ...getCrpColumns(t),
  returnSingleSelectColumn(
    'fundedBy',
    'Funded by',
    getOptionsForTable(t, 'ppm.fundedBy.options', true),
    'ppm.fundedBy.options',
    t,
  ),
  returnSingleSelectColumn(
    'ocnPriority',
    'OCN Priority',
    getOptionsForTable(t, 'ocnPriority', true),
    'ocnPriority',
    t,
  ),
  returnSingleSelectColumn(
    'subPortfolio',
    t('ppm.columns.subPortfolio'),
    getOptionsForTable(t, 'ppm.subportfolios', true),
    'ppm.subportfolios',
    t,
  ),
  {
    field: 'isep',
    headerName: t('ppm.columns.isep'),
    minWidth: 100,
    display: 'flex',
    aggregable: false,
    type: 'singleSelect',
    valueOptions: booleanSelectOptions,
    renderCell: (params) =>
      params.value ? (
        <Badge label={t('common.yes')} colour="green" size="small" />
      ) : (
        <Badge label={t('common.no')} colour="white" size="small" />
      ),
  },
  {
    field: 'confidential',
    headerName: 'Confidentiality',
    type: 'singleSelect',
    valueOptions: getOptionsForTable(t, 'confidentialityLevels', true),
    aggregable: false,
    minWidth: 180,
    display: 'flex',
    valueGetter: (value) =>
      getTranslatedValue(value, 'confidentialityLevels', t),
    groupingValueGetter: (value) =>
      getTranslatedValue(value, 'confidentialityLevels', t),
    renderCell: (params) =>
      params.row?.confidential ? (
        <Badge
          label={params.value}
          colour={confidentialityColourMap[params.row.confidential] || 'grey'}
          size="small"
        />
      ) : null,
  },
  {
    field: 'alignedGEXFY25',
    headerName: 'GEX FY25',
    minWidth: 100,
    renderCell: (params: GridRenderCellParams) => {
      return params.row.alignedWithGEXFY?.['FY25'] || '';
    },
  },
  {
    field: 'alignedGEXFY26',
    headerName: 'GEX FY26',
    minWidth: 100,
    renderCell: (params: GridRenderCellParams) => {
      return params.row.alignedWithGEXFY?.['FY26'] || '';
    },
  },
  {
    field: 'alignedGEXDatesFY25',
    headerName: 'GEX Dates FY25',
    minWidth: 200,
    renderCell: (params: GridRenderCellParams) => {
      return params.row.alignedWithGEXFYDates?.['FY25'] || '';
    },
  },
  {
    field: 'alignedGEXDatesFY26',
    headerName: 'GEX Dates FY26',
    minWidth: 200,
    renderCell: (params: GridRenderCellParams) => {
      return params.row.alignedWithGEXFYDates?.['FY26'] || '';
    },
  },
  {
    field: 'baselineFY26',
    headerName: 'Baseline FY26',
    display: 'flex',
    minWidth: 200,
    renderCell: (params: GridRenderCellParams) => (
      <Typography noWrap>
        {accounting.formatMoney(params.row.baselineByFY?.['FY26'] || '', {
          precision: 0,
          symbol: params?.row?.currencySymbol || '',
        })}
      </Typography>
    ),
  },
  {
    field: 'landCostFY26',
    display: 'flex',
    headerName: 'Baseline Land Cost FY26',
    minWidth: 200,
    renderCell: (params: GridRenderCellParams) => (
      <Typography noWrap>
        {accounting.formatMoney(params.row['baseline-FY26-landCost'] ?? 0, {
          precision: 0,
          symbol: params?.row?.currencySymbol || '',
        })}
      </Typography>
    ),
  },
  {
    field: 'propertyInvestmentFY26',
    display: 'flex',
    headerName: 'Baseline property Investment FY26',
    minWidth: 200,
    renderCell: (params: GridRenderCellParams) => (
      <Typography noWrap>
        {accounting.formatMoney(
          params.row['baseline-FY26-propertyInvestment'] ?? 0,
          {
            precision: 0,
            symbol: params?.row?.currencySymbol || '',
          },
        )}
      </Typography>
    ),
  },
  {
    field: 'retailInvestmentFY26',
    headerName: 'Baseline Retail Investment FY26',
    display: 'flex',
    minWidth: 200,
    renderCell: (params: GridRenderCellParams) => (
      <Typography noWrap>
        {accounting.formatMoney(
          params.row['baseline-FY26-retailInvestment'] ?? 0,
          {
            precision: 0,
            symbol: params?.row?.currencySymbol || '',
          },
        )}
      </Typography>
    ),
  },
  {
    field: 'retailCostFY26',
    headerName: 'Baseline Retail Cost FY26',
    display: 'flex',
    minWidth: 200,
    renderCell: (params: GridRenderCellParams) => (
      <Typography noWrap>
        {accounting.formatMoney(params.row['baseline-FY26-retailCost'] ?? 0, {
          precision: 0,
          symbol: params?.row?.currencySymbol || '',
        })}
      </Typography>
    ),
  },
  {
    field: 'partOfFda',
    headerName: t('ppm.columns.partOfFda'),
    minWidth: 150,
    display: 'flex',
    type: 'singleSelect',
    valueOptions: fdaTableOptions(t),
    aggregable: false,
    valueGetter: (value) => getFdaValue(value, t),
    groupingValueGetter: (value) => getFdaValue(value, t),
    renderCell: defaultRenderer,
  },
  getBooleanColumn('partOfOnePlan', t('ppm.columns.partOfOnePlan'), t),
  getBooleanColumn('realEstateSISAN', t('ppm.columns.realEstateSISAN'), t),

  ...getFinancialColumns(t),
  ...getNextForecastColumns(t),
  ...getFutureFyForecastColumns(t),
  {
    field: FY_BUDGET_FILTER_FIELD,
    headerName: 'FY Budget FYs - Utility',
    filterable: true,
    sortable: false,
    groupable: false,
    aggregable: false,
    filterOperators: [fyBudgetFilterOperator],
  } as GridColDef,
  {
    field: 'countryCode',
    headerName: 'Country Code',
  },
  {
    field: 'isDirectiveComplete',
    headerName: 'Project Directive',
    minWidth: 200,
    display: 'flex',
    aggregable: false,
    type: 'singleSelect',
    cellClassName: 'directive-cell--overflow-visible',
    valueOptions: [
      { value: true, label: 'Completed' },
      { value: false, label: 'Not completed' },
    ],

    groupingValueGetter: (value) => {
      return value ? 'Completed' : 'Not completed';
    },
    renderCell: (params: GridRenderCellParams) => {
      if (params.value === null || params.value === undefined)
        return (
          <Typography noWrap style={{ margin: 'auto' }}>
            -
          </Typography>
        );
      const label = params.value ? 'Completed' : 'Not completed';
      if (params.rowNode?.type === 'group') {
        return <Typography noWrap>{label}</Typography>;
      }
      return (
        <IngkaTooltip label={'View project directive'}>
          <Button
            type="tertiary"
            size="xsmall"
            ssrIcon={params.value ? documentCheckmark : document}
            iconPosition="leading"
            text={label}
            onClick={(e) => {
              e.stopPropagation();
              directiveClickedEvent?.(params.row.referenceId);
            }}
          />
        </IngkaTooltip>
      );
    },
  },
];

export const DEFAULT_VIEW_COLUMNS = [
  '__check__',
  'projectStatus',
  'referenceId',
  'projectName',
  'establishmentType',
  'unitType',
  'targetProjectStartDate',
  'targetProjectEndDate',
  'fyBaselineAmount',
  'partOfCrpPlan',
  'partOfApprovedCRP',
  'isep',
  'confidential',
  'isDirectiveComplete',
];

export const FINANCIAL_VIEW_COLUMNS = [
  '__check__',
  'id',
  'projectName',
  'dpProgress',
  'isepDate',
  ...FinancialColumns,
  ...columnsByFy(),
];

export const FORECAST_VIEW_COLUMNS = [
  '__check__',
  'id',
  'projectName',
  'dpProgress',
  'isepDate',
  ...NextFyForecast,
];

export const viewClicked = (view, tableSettings, t) => {
  const visibilityObject = {};
  const allColumns = tableSettings.datagridProps.apiRef.current.getAllColumns();
  let columnsToShow = [];
  const columnsToHide = allColumns.map((col) => col.field);
  switch (view) {
    case 'default':
      columnsToShow = DEFAULT_VIEW_COLUMNS;
      tableSettings.datagridProps.apiRef.current.deleteFilterItem({
        id: `LADA`,
        field: 'unitType',
        operator: 'isAnyOf',
      });
      break;
    case 'financial':
      columnsToShow = FINANCIAL_VIEW_COLUMNS;
      tableSettings.datagridProps.apiRef.current.deleteFilterItem({
        id: `LADA`,
        field: 'unitType',
        operator: 'isAnyOf',
      });
      break;
    case 'lada':
      columnsToShow = DEFAULT_VIEW_COLUMNS;
      tableSettings.datagridProps.apiRef.current.upsertFilterItem({
        id: `LADA`,
        field: 'unitType',
        operator: 'isAnyOf',
        value: [
          t('unitTypes.ikeaSmallStoreLadaS'),
          t('unitTypes.ikeaSmallStoreLadaM'),
        ],
      });
      break;
    case 'forecast':
    default:
      tableSettings.datagridProps.apiRef.current.deleteFilterItem({
        id: `LADA`,
        field: 'unitType',
        operator: 'isAnyOf',
      });
      columnsToShow = FORECAST_VIEW_COLUMNS;
  }

  columnsToHide.forEach((column) => {
    visibilityObject[column] = false;
  });
  columnsToShow.forEach((column) => {
    visibilityObject[column] = true;
  });
  tableSettings.datagridProps.onColumnVisibilityModelChange(visibilityObject);
};
